import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { uploadUrl: String }
  static targets = [
    "canvas", "canvasViewport", "canvasScaler", "canvasOutput",
    "propertiesPanel", "textProperties", "imageProperties", "generalProperties", "canvasProperties",
    "textColor", "fontSize", "fontSizeValue", "fontFamily", "boldBtn", "italicBtn", "textWidth", "textHeight",
    "imageFile", "animation", "animationDuration", "animationDelay", "canvasBackgroundColor", "canvasBackgroundImage", "canvasBackgroundImageMode", "canvasBackgroundImageRemove",
    "deleteButton", "duplicateButton", "zIndexControls", "actionsProperties",
    "elementBackgroundColor", "elementPadding", "elementBorderRadius", "elementPaddingRow", "elementPaddingLabel",
    "animationProperties", "shadowProperties",
    "elementShadowColor", "elementShadowBlur", "elementShadowAngle",
    "toolText", "toolImage", "toolShape", "toolCircle"
  ]

  connect() {
    this.selectedElement = null
    this.elementIdCounter = 0
    this.activePlacementTool = null
    this.pendingImagePosition = null
    this.pendingDuplicateElement = null
    this.canvasScale = 1
    this.isResizing = false
    this.isRotating = false
    this.resizeHandle = null
    this.startX = 0
    this.startY = 0
    this.startWidth = 0
    this.startHeight = 0
    this.startRotation = 0
    this.startAngle = 0

    // Event listeners voor resize en rotatie
    this.mousemoveHandler = this.handleMouseMove.bind(this)
    this.mouseupHandler = this.handleMouseUp.bind(this)

    // Update panel positie tijdens slepen
    this.panelUpdateRaf = null
    this.panelUpdateHandler = () => {
      if (this.selectedElement && this.selectedElement.classList.contains('editor__dragging')) {
        if (!this.panelUpdateRaf) {
          this.updatePanelDuringDrag()
        }
      }
    }

    this.updatePanelDuringDrag = () => {
      if (this.selectedElement && this.selectedElement.classList.contains('editor__dragging')) {
        this.updatePanelPosition(this.selectedElement)
        this.panelUpdateRaf = requestAnimationFrame(this.updatePanelDuringDrag)
      } else {
        this.panelUpdateRaf = null
      }
    }

    this._serializeDebounce = null

    // Luister naar mouse move events voor panel updates
    document.addEventListener('mousemove', this.panelUpdateHandler, { passive: true })

    this._panelRepositionRaf = null
    this._removePanelRepositionListeners = (el) => {
      if (!el || el.dataset.elementType !== 'text') return
      const span = el.querySelector('span[contenteditable="true"]')
      if (span) {
        span.removeEventListener('input', this._panelRepositionOnText)
        span.removeEventListener('selectionchange', this._panelRepositionOnText)
      }
    }
    this._panelRepositionOnText = () => {
      if (this._panelRepositionRaf) return
      this._panelRepositionRaf = requestAnimationFrame(() => {
        if (this.selectedElement && this.selectedElement.dataset.elementType === 'text') {
          this.updatePanelPosition(this.selectedElement)
        }
        this._panelRepositionRaf = null
      })
    }

    // Laad bestaande template uit hidden field (indien gevuld)
    this._loadFromHiddenField()

    // Verwerk bestaande elementen in het canvas na een korte delay
    // Dit zorgt ervoor dat Stimulus volledig geladen is
    setTimeout(() => {
      this.processExistingElements()
      this.serializeCanvas()
    }, 100)

    // Houd hidden field in sync bij wijzigingen op het canvas (toevoegen, verwijderen, stijl, tekst)
    if (this.canvasTarget) {
      const observer = new MutationObserver(() => this._scheduleSerialize())
      observer.observe(this.canvasTarget, { childList: true, subtree: true, attributes: true, characterData: true })
      this._canvasMutationObserver = observer
    }

    this._escapeHandler = (e) => {
      if (e.key === 'Escape' && this.pendingDuplicateElement) {
        this.pendingDuplicateElement = null
        this.element.classList.remove('editor__place-duplicate')
      }
    }
    document.addEventListener('keydown', this._escapeHandler)

    this._contextMenuHandler = (e) => e.preventDefault()
    this.element.addEventListener('contextmenu', this._contextMenuHandler)

    const doScale = () => {
      this.updateCanvasScale()
    }
    doScale()
    requestAnimationFrame(doScale)
    // Gebruik requestAnimationFrame om layout thrash en ResizeObserver-warnings te voorkomen
    window.addEventListener('resize', () => {
      window.requestAnimationFrame(() => this.updateCanvasScale())
    })
    // ResizeObserver: artboard past zich aan bij flex layout wijzigingen (b.v. sidebar toggle)
    const container = this.hasCanvasViewportTarget ? this.canvasViewportTarget.parentElement : null
    if (container && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.updateCanvasScale())
      })
      this._resizeObserver.observe(container)
    }
  }

  _loadFromHiddenField() {
    if (!this.hasCanvasOutputTarget || !this.canvasTarget) return
    const value = (this.canvasOutputTarget.value || '').trim()
    if (!value) return
    try {
      const doc = new DOMParser().parseFromString(value, 'text/html')
      const root = doc.body.firstElementChild
      if (!root) return
      // Serialized format: wrapper met data-canvas-background en child-elementen
      if (root.hasAttribute('data-canvas-background')) {
        const bg = root.getAttribute('data-canvas-background')
        if (bg) this.canvasTarget.style.backgroundColor = bg
        const bgImage = root.getAttribute('data-canvas-background-image')
        const bgMode = root.getAttribute('data-canvas-background-image-mode')
        if (bgImage) {
          this.canvasTarget.dataset.backgroundImage = bgImage
          this.canvasTarget.dataset.backgroundImageMode = bgMode || 'pattern'
          this._applyCanvasBackgroundImage()
        }
        while (root.firstChild) {
          this.canvasTarget.appendChild(root.firstChild)
        }
      } else {
        // Platte format: root(s) zijn de elementen zelf
        while (doc.body.firstChild) {
          this.canvasTarget.appendChild(doc.body.firstChild)
        }
      }
    } catch (e) {
      console.warn('Signage editor: kon template niet laden uit hidden field', e)
    }
  }

  updateCanvasScale() {
    if (!this.hasCanvasViewportTarget || !this.hasCanvasScalerTarget) return
    const CANVAS_W = 1920
    const CANVAS_H = 1080
    const container = this.canvasViewportTarget.parentElement
    if (!container) return
    const w = container.clientWidth
    const h = container.clientHeight
    const scale = Math.min(w / CANVAS_W, h / CANVAS_H, 1)
    this.canvasScale = scale
    this.canvasTarget.dataset.scale = scale
    this.canvasViewportTarget.style.width = (CANVAS_W * scale) + 'px'
    this.canvasViewportTarget.style.height = (CANVAS_H * scale) + 'px'
    this.canvasScalerTarget.style.transform = `scale(${scale})`
  }

  togglePanelSection(event) {
    const section = event.currentTarget.dataset.section
    if (!section) return
    this._openPanelSection(section)
  }

  _openPanelSection(section) {
    const sections = {
      text: this.hasTextPropertiesTarget ? this.textPropertiesTarget : null,
      image: this.hasImagePropertiesTarget ? this.imagePropertiesTarget : null,
      general: this.hasGeneralPropertiesTarget ? this.generalPropertiesTarget : null,
      animation: this.hasAnimationPropertiesTarget ? this.animationPropertiesTarget : null,
      shadow: this.hasShadowPropertiesTarget ? this.shadowPropertiesTarget : null,
      canvas: this.hasCanvasPropertiesTarget ? this.canvasPropertiesTarget : null,
      zindex: this.hasZIndexControlsTarget ? this.zIndexControlsTarget : null,
      actions: this.hasActionsPropertiesTarget ? this.actionsPropertiesTarget : null
    }
    Object.entries(sections).forEach(([key, block]) => {
      if (!block) return
      if (block.style.display === 'none') return
      if (key === section) {
        block.classList.remove('editor__panel-block--collapsed')
      } else {
        block.classList.add('editor__panel-block--collapsed')
      }
    })
  }

  setActiveTool(tool) {
    this.activePlacementTool = tool
    const tools = [
      { key: 'text', target: this.hasToolTextTarget ? this.toolTextTarget : null },
      { key: 'image', target: this.hasToolImageTarget ? this.toolImageTarget : null },
      { key: 'shape', target: this.hasToolShapeTarget ? this.toolShapeTarget : null },
      { key: 'circle', target: this.hasToolCircleTarget ? this.toolCircleTarget : null }
    ]
    tools.forEach(({ key, target }) => {
      if (target) {
        if (key === tool) target.classList.add('editor__active')
        else target.classList.remove('editor__active')
      }
    })

    // Visuele feedback: crosshair-cursor over canvas wanneer een tool actief is
    if (tool) {
      this.element.classList.add('editor__tool-active')
    } else {
      this.element.classList.remove('editor__tool-active')
    }
  }

  selectToolText() {
    if (this.activePlacementTool === 'text') {
      this.setActiveTool(null)
      return
    }
    this.setActiveTool('text')
  }

  selectToolImage() {
    if (this.activePlacementTool === 'image') {
      this.setActiveTool(null)
      return
    }
    this.setActiveTool('image')
  }

  selectToolShape() {
    if (this.activePlacementTool === 'shape') {
      this.setActiveTool(null)
      return
    }
    this.setActiveTool('shape')
  }

  selectToolCircle() {
    if (this.activePlacementTool === 'circle') {
      this.setActiveTool(null)
      return
    }
    this.setActiveTool('circle')
  }

  startDuplicate() {
    if (!this.selectedElement) return
    this.pendingDuplicateElement = this.selectedElement
    this.element.classList.add('editor__place-duplicate')
  }

  placeDuplicateAt(canvasX, canvasY) {
    if (!this.pendingDuplicateElement || !this.canvasTarget) return
    const source = this.pendingDuplicateElement
    const clone = source.cloneNode(true)
    clone.dataset.elementId = this.elementIdCounter++
    const w = source.offsetWidth || parseFloat(source.style.width) || 100
    const h = source.offsetHeight || parseFloat(source.style.height) || 100
    clone.style.left = (canvasX - w / 2) + 'px'
    clone.style.top = (canvasY - h / 2) + 'px'
    clone.classList.remove('editor__selected')
    // Bij afbeelding: zorg voor image-wrap als die ontbreekt (oude structuur)
    if (source.dataset.elementType === 'image') {
      const img = clone.querySelector('img')
      if (img && !clone.querySelector('.editor__image-wrap')) {
        const wrap = document.createElement('div')
        wrap.className = 'editor__image-wrap'
        img.parentNode.insertBefore(wrap, img)
        wrap.appendChild(img)
      }
    }
    // Handles hebben geen listeners na clone; vervangen door nieuwe
    clone.querySelectorAll('.editor__resize-handle, .editor__rotate-handle').forEach((el) => el.remove())
    this.addResizeHandles(clone)
    this.addRotateHandle(clone)
    clone.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = clone.dataset.wasDragged === 'true'
        if (wasDragging) return
        const span = clone.querySelector('span')
        if (clone.dataset.elementType === 'text' && this.selectedElement === clone && span && span.contains(e.target)) {
          span.contentEditable = 'true'
          span.focus()
          e.preventDefault()
          e.stopPropagation()
          return
        }
        this.selectElement(e, clone)
      }
    })
    this.canvasTarget.appendChild(clone)
    this.pendingDuplicateElement = null
    this.element.classList.remove('editor__place-duplicate')
    this.selectElement(null, clone)
  }

  placeElementAt(canvasX, canvasY) {
    if (!this.activePlacementTool) return
    switch (this.activePlacementTool) {
      case 'text':
        this.addText({ x: canvasX, y: canvasY })
        this.setActiveTool(null)
        break
      case 'shape':
        this.addShape({ x: canvasX, y: canvasY })
        this.setActiveTool(null)
        break
      case 'circle':
        this.addCircle({ x: canvasX, y: canvasY })
        this.setActiveTool(null)
        break
      case 'image':
        this.pendingImagePosition = { x: canvasX, y: canvasY }
        this.openImageFileDialog()
        this.setActiveTool(null)
        break
    }
  }

  processExistingElements() {
    const canvas = this.canvasTarget
    if (!canvas) {
      return
    }

    // Vind alle directe child divs die nog niet verwerkt zijn
    const existingDivs = Array.from(canvas.children).filter(child => {
      return child.tagName === 'DIV' && !child.classList.contains('editor__editable-element')
    })

    existingDivs.forEach(div => {
      const span = div.querySelector('span')
      const img = div.querySelector('img')
      const elementType = div.dataset.elementType

      if (elementType === 'shape') {
        this.processShapeElement(div)
      } else if (elementType === 'circle') {
        this.processCircleElement(div)
      } else if (span) {
        this.processTextElement(div, span)
      } else if (img) {
        this.processImageElement(div, img)
      } else if (!span && !img) {
        // Div zonder span/img: vorm of cirkel (bijv. zonder data-element-type)
        const isCircle = (div.style.borderRadius || '').indexOf('50%') !== -1
        if (isCircle) this.processCircleElement(div)
        else this.processShapeElement(div)
      }
    })
  }

  processTextElement(div, span) {
    // Zet juiste classes en attributes
    div.className = 'editor__editable-element'
    div.dataset.controller = 'draggable'
    div.dataset.elementType = 'text'
    div.dataset.elementId = this.elementIdCounter++

    // Zorg voor position absolute
    if (!div.style.position) {
      div.style.position = 'absolute'
    }

    // Tekst pas bewerkbaar na bewust klikken op de tekst (zie click handler)
    span.contentEditable = 'false'

    this.addResizeHandles(div)
    this.addRotateHandle(div)

    // Standaard styling als er geen is
    if (!span.style.fontSize) {
      span.style.fontSize = '16px'
    }
    if (!span.style.color) {
      span.style.color = '#000000'
    }
    if (!span.style.fontFamily) {
      span.style.fontFamily = 'Arial'
    }

    // Klik: alleen selecteren als niet gesleept; tekst pas bewerkbaar bij tweede klik op de tekst
    div.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = div.dataset.wasDragged === 'true'
        if (wasDragging) return
        const span = div.querySelector('span')
        if (this.selectedElement === div && span && span.contains(e.target)) {
          span.contentEditable = 'true'
          span.focus()
          e.preventDefault()
          e.stopPropagation()
          return
        }
        this.selectElement(e, div)
      }
    })

    // Alleen een rotate handle voor tekst (geen resize)
    this.addRotateHandle(div)
  }

  _sanitizeImageUrl(url) {
    if (!url || typeof url !== 'string') return url
    return url.trim().replace(/^["']+|["']+$/g, '')
  }

  /** Font-family uit style kan quotes bevatten (bijv. "Times New Roman"); normaliseer voor select value. */
  _normalizeFontFamily(fontFamily) {
    if (!fontFamily || typeof fontFamily !== 'string') return fontFamily
    return fontFamily.trim().replace(/^["']+|["']+$/g, '')
  }

  processImageElement(div, img) {
    // Fix img src: strip eventuele omringende quotes (bijv. door JSON-encoding)
    const rawSrc = img.getAttribute('src') || img.src
    if (rawSrc) {
      img.src = this._sanitizeImageUrl(rawSrc)
    }
    // Zet juiste classes en attributes
    div.className = 'editor__editable-element'
    div.dataset.controller = 'draggable'
    div.dataset.elementType = 'image'
    div.dataset.elementId = this.elementIdCounter++

    // Zorg voor position absolute
    if (!div.style.position) {
      div.style.position = 'absolute'
    }

    // Wrap img in inner div zodat rotate-handle zichtbaar blijft (geen overflow:hidden op outer)
    if (!div.querySelector('.editor__image-wrap')) {
      const wrap = document.createElement('div')
      wrap.className = 'editor__image-wrap'
      img.parentNode.insertBefore(wrap, img)
      wrap.appendChild(img)
    }

    // Zorg dat img styling correct is
    if (!img.style.objectFit) {
      // Gebruik 'cover' zodat er geen lege balken rondom de afbeelding ontstaan,
      // maar de verhoudingen wel behouden blijven (eventueel met lichte crop).
      img.style.objectFit = 'cover'
    }
    img.style.display = 'block'
    // Schakel native browser drag van de afbeelding uit; we gebruiken onze eigen drag-logica
    img.draggable = false

    // Bepaal of dit element al een expliciete grootte heeft (bijv. uit opgeslagen template)
    const hasExplicitSize = !!(div.style.width || div.style.height)
    if (!hasExplicitSize) {
      const adjust = () => {
        this.adjustDivToImage(div, img)
      }
      if (img.complete) {
        adjust()
      } else {
        img.onload = () => {
          adjust()
          img.onload = null
        }
      }
    }

    // Event listener voor selectie
    div.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = div.dataset.wasDragged === 'true'
        if (!wasDragging) {
          this.selectElement(e, div)
        }
      }
    })

    // Voeg resize handles en rotate handle toe
    this.addResizeHandles(div)
    this.addRotateHandle(div)

    // Stimulus pikt wijzigingen in data-controller automatisch op via MutationObserver,
    // dus we hoeven hier niets expliciet te laden.
  }

  processShapeElement(div) {
    div.className = 'editor__editable-element'
    div.dataset.controller = 'draggable'
    div.dataset.elementType = 'shape'
    div.dataset.elementId = this.elementIdCounter++
    if (!div.style.position) div.style.position = 'absolute'
    if (!div.style.width) div.style.width = '200px'
    if (!div.style.height) div.style.height = '200px'
    if (!div.style.backgroundColor) div.style.backgroundColor = '#ffffff'
    if (!div.style.borderRadius) div.style.borderRadius = '0px'

    div.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = div.dataset.wasDragged === 'true'
        if (!wasDragging) this.selectElement(e, div)
      }
    })
    this.addResizeHandles(div)
    this.addRotateHandle(div)
  }

  processCircleElement(div) {
    div.className = 'editor__editable-element'
    div.dataset.controller = 'draggable'
    div.dataset.elementType = 'circle'
    div.dataset.elementId = this.elementIdCounter++
    if (!div.style.position) div.style.position = 'absolute'
    if (!div.style.width) div.style.width = '200px'
    if (!div.style.height) div.style.height = '200px'
    if (!div.style.backgroundColor) div.style.backgroundColor = '#ffffff'
    if (!div.style.borderRadius) div.style.borderRadius = '50%'

    div.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = div.dataset.wasDragged === 'true'
        if (!wasDragging) this.selectElement(e, div)
      }
    })
    this.addResizeHandles(div)
    this.addRotateHandle(div)
  }

  // Hulpfunctie: zorg dat de wrapper-div netjes om de afbeelding heen past
  // en beperk de afmetingen indien nodig tot binnen het canvas, met een
  // prettige standaardgrootte (max. 400px breed).
  /**
   * Schaalt een afbeeldingsbestand naar maximaal 1920×1080 (aspect ratio behouden).
   * Geeft de daadwerkelijk verkleinde pixeldata terug als Blob, zodat de bestandsgrootte kleiner wordt.
  /**
   * Upload afbeelding naar de ingestelde uploadUrl (Rails backend).
   * Gebruikt CSRF token uit meta[name="csrf-token"].
   * Verwacht JSON response met { url: "..." } of { src: "..." }, of plain text met de URL.
   */
  _uploadImage(blob) {
    const url = this.uploadUrlValue
    if (!url) return Promise.reject(new Error('Geen uploadUrl geconfigureerd'))
    const ext = { 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp' }[blob.type] || 'jpg'
    const formData = new FormData()
    formData.append('file', blob, `image.${ext}`)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      formData.append('authenticity_token', csrfToken)
    }
    const headers = {}
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }
    headers['X-Requested-With'] = 'XMLHttpRequest'
    headers['Accept'] = 'application/json'
    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'same-origin'
    }).then((res) => {
      if (!res.ok) throw new Error(`Upload mislukt: ${res.status}`)
      const contentType = res.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        return res.json().then((data) => {
          const u = data.url || data.src || data.data?.url || data.data?.src
          return u ? this._sanitizeImageUrl(String(u)) : u
        })
      }
      return res.text()
    }).then((result) => {
      if (typeof result === 'string' && result.trim()) return this._sanitizeImageUrl(result.trim())
      throw new Error('Geen URL in response')
    })
  }

  adjustDivToImage(div, img) {
    const canvas = this.canvasTarget || div.closest('.editor__canvas')
    const canvasWidth = canvas ? canvas.offsetWidth : Infinity
    const canvasHeight = canvas ? canvas.offsetHeight : Infinity

    // Bepaal ruwe afbeelding-afmetingen
    const naturalWidth = img.naturalWidth || img.width || img.clientWidth || 100
    const naturalHeight = img.naturalHeight || img.height || img.clientHeight || 100

    let width = naturalWidth
    let height = naturalHeight

    // Maximaal 400px breed en binnen het canvas (90% van canvas)
    const maxLogicalWidth = 400
    const maxWidth = Math.min(canvasWidth * 0.9, maxLogicalWidth)
    const maxHeight = canvasHeight * 0.9

    const widthRatio = maxWidth / width
    const heightRatio = maxHeight / height
    const scale = Math.min(1, widthRatio, heightRatio)

    width *= scale
    height *= scale

    // Pas zowel img als wrapper-div aan
    img.style.width = width + 'px'
    img.style.height = height + 'px'
    div.style.width = width + 'px'
    div.style.height = height + 'px'
  }

  disconnect() {
    document.removeEventListener('mousemove', this.panelUpdateHandler)
    document.removeEventListener('keydown', this._escapeHandler)
    this.element.removeEventListener('contextmenu', this._contextMenuHandler)
    if (this.panelUpdateRaf) {
      cancelAnimationFrame(this.panelUpdateRaf)
      this.panelUpdateRaf = null
    }
    if (this._serializeDebounce) clearTimeout(this._serializeDebounce)
    if (this._canvasMutationObserver) this._canvasMutationObserver.disconnect()
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    document.body.classList.remove('editor__no-select')
    if (this._selectStartHandler) {
      document.removeEventListener('selectstart', this._selectStartHandler)
    }
  }

  addText(position) {
    const element = document.createElement('div')
    element.className = 'editor__editable-element'
    element.dataset.controller = 'draggable'
    element.dataset.elementType = 'text'
    element.dataset.elementId = this.elementIdCounter++

    const span = document.createElement('span')
    //span.textContent = 'Nieuwe tekst'
    span.contentEditable = true
    element.appendChild(span)

    setTimeout(() => { span.focus() }, 100)

    // Standaard styling
    span.style.fontSize = '16px'
    span.style.color = '#000000'
    span.style.fontFamily = 'Arial'

    const canvas = this.canvasTarget
    if (position && typeof position.x === 'number' && typeof position.y === 'number') {
      element.style.left = (position.x - 50) + 'px'
      element.style.top = (position.y - 12) + 'px'
    } else {
      element.style.left = (canvas.offsetWidth / 2 - 50) + 'px'
      element.style.top = (canvas.offsetHeight / 2 - 20) + 'px'
    }

    // Klik: alleen selecteren als niet gesleept; tekst pas bewerkbaar bij tweede klik op de tekst
    element.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = element.dataset.wasDragged === 'true'
        if (wasDragging) return
        const span = element.querySelector('span')
        if (this.selectedElement === element && span && span.contains(e.target)) {
          span.contentEditable = 'true'
          span.focus()
          e.preventDefault()
          e.stopPropagation()
          return
        }
        this.selectElement(e, element)
      }
    })

    this.addResizeHandles(element)
    this.addRotateHandle(element)
    canvas.appendChild(element)

    // Stimulus initialiseert nieuwe elementen automatisch zodra ze aan de DOM
    // worden toegevoegd, dus geen expliciete load nodig.

    this.selectElement(null, element)
  }

  addShape(position) {
    const canvas = this.canvasTarget
    if (!canvas) {
      console.error('Canvas niet gevonden')
      return
    }

    const defaultSize = 200
    const element = document.createElement('div')
    element.className = 'editor__editable-element'
    element.dataset.controller = 'draggable'
    element.dataset.elementType = 'shape'
    element.dataset.elementId = this.elementIdCounter++
    element.style.position = 'absolute'
    element.style.width = defaultSize + 'px'
    element.style.height = defaultSize + 'px'
    element.style.backgroundColor = '#ffffff'
    element.style.borderRadius = '0px'

    if (position && typeof position.x === 'number' && typeof position.y === 'number') {
      element.style.left = (position.x - defaultSize / 2) + 'px'
      element.style.top = (position.y - defaultSize / 2) + 'px'
    } else {
      element.style.left = (canvas.offsetWidth / 2 - defaultSize / 2) + 'px'
      element.style.top = (canvas.offsetHeight / 2 - defaultSize / 2) + 'px'
    }

    element.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = element.dataset.wasDragged === 'true'
        if (!wasDragging) {
          this.selectElement(e, element)
        }
      }
    })

    this.addResizeHandles(element)
    this.addRotateHandle(element)
    canvas.appendChild(element)
    this.selectElement(null, element)
  }

  openImageFileDialog() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.style.display = 'none'

    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        const objectUrl = URL.createObjectURL(file)
        const canvas = this.canvasTarget || this.element.querySelector('[data-editor-target="canvas"]')
        if (!canvas) {
          console.error('Canvas niet gevonden')
          URL.revokeObjectURL(objectUrl)
          return
        }

        const element = document.createElement('div')
        element.className = 'editor__editable-element'
        element.dataset.controller = 'draggable'
        element.dataset.elementType = 'image'
        element.dataset.elementId = this.elementIdCounter++
        element.style.position = 'absolute'

        const wrap = document.createElement('div')
        wrap.className = 'editor__image-wrap'
        const img = document.createElement('img')
        img.style.objectFit = 'cover'
        img.style.display = 'block'
        img.draggable = false
        img.src = objectUrl
        wrap.appendChild(img)
        element.appendChild(wrap)

        element.addEventListener('click', (e) => {
          if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
            const wasDragging = element.dataset.wasDragged === 'true'
            if (!wasDragging) {
              this.selectElement(e, element)
            }
          }
        })

        this.addResizeHandles(element)
        this.addRotateHandle(element)
        canvas.appendChild(element)
        this._scheduleSerialize()

        const setImageSrcAndPosition = (src) => {
          img.onload = () => {
            this.adjustDivToImage(element, img)
            const imgWidth = parseFloat(element.style.width) || img.clientWidth || img.naturalWidth || 0
            const imgHeight = parseFloat(element.style.height) || img.clientHeight || img.naturalHeight || 0
            if (this.pendingImagePosition) {
              element.style.left = (this.pendingImagePosition.x - imgWidth / 2) + 'px'
              element.style.top = (this.pendingImagePosition.y - imgHeight / 2) + 'px'
              this.pendingImagePosition = null
            } else {
              element.style.left = (canvas.offsetWidth / 2 - imgWidth / 2) + 'px'
              element.style.top = (canvas.offsetHeight / 2 - imgHeight / 2) + 'px'
            }
            img.onload = null
            URL.revokeObjectURL(objectUrl)
            this._scheduleSerialize()
          }
          img.src = src
          if (img.complete) img.onload()
        }
        if (this.hasUploadUrlValue && this.uploadUrlValue && this.uploadUrlValue.trim()) {
          this._uploadImage(file).then((url) => setImageSrcAndPosition(url)).catch((err) => {
            console.error('Upload mislukt:', err)
            alert('De afbeelding kon niet worden geüpload.')
            element.remove()
          })
        } else {
          const reader = new FileReader()
          reader.onloadend = () => {
            img.onload = null
            setImageSrcAndPosition(reader.result)
          }
          reader.readAsDataURL(file)
        }
        this.selectElement(null, element)

        img.onerror = () => {
          console.error('Error loading image')
          alert('De afbeelding kon niet worden geladen.')
          URL.revokeObjectURL(objectUrl)
        }
      }
      input.value = ''
    }

    // Voeg input toe aan body en klik
    document.body.appendChild(input)
    input.click()

    // Verwijder input na gebruik
    setTimeout(() => {
      if (input.parentNode) {
        input.parentNode.removeChild(input)
      }
    }, 1000)
  }

  addImage() {
    this.openImageFileDialog()
  }

  addCircle(position) {
    const canvas = this.canvasTarget || this.element.querySelector('[data-editor-target="canvas"]')
    if (!canvas) {
      console.error('Canvas niet gevonden')
      return
    }

    const defaultSize = 200
    const element = document.createElement('div')
    element.className = 'editor__editable-element'
    element.dataset.controller = 'draggable'
    element.dataset.elementType = 'circle'
    element.dataset.elementId = this.elementIdCounter++
    element.style.position = 'absolute'
    element.style.width = defaultSize + 'px'
    element.style.height = defaultSize + 'px'
    element.style.backgroundColor = '#ffffff'
    element.style.borderRadius = '50%'

    if (position && typeof position.x === 'number' && typeof position.y === 'number') {
      element.style.left = (position.x - defaultSize / 2) + 'px'
      element.style.top = (position.y - defaultSize / 2) + 'px'
    } else {
      element.style.left = (canvas.offsetWidth / 2 - defaultSize / 2) + 'px'
      element.style.top = (canvas.offsetHeight / 2 - defaultSize / 2) + 'px'
    }

    element.addEventListener('click', (e) => {
      if (!e.target.closest('.editor__resize-handle') && !e.target.closest('.editor__rotate-handle')) {
        const wasDragging = element.dataset.wasDragged === 'true'
        if (!wasDragging) {
          this.selectElement(e, element)
        }
      }
    })

    this.addResizeHandles(element)
    this.addRotateHandle(element)
    canvas.appendChild(element)
    this.selectElement(null, element)
  }

  addResizeHandles(element) {
    if (element.dataset.elementType === 'text') return
    const handles = ['nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e']
    handles.forEach(handle => {
      const handleEl = document.createElement('div')
      handleEl.className = `editor__resize-handle editor__${handle}`
      handleEl.addEventListener('mousedown', (e) => {
        e.stopPropagation()
        this.startResize(e, element, handle)
      })
      element.appendChild(handleEl)
    })
  }

  addRotateHandle(element) {
    const handleEl = document.createElement('div')
    handleEl.className = 'editor__rotate-handle'
    handleEl.addEventListener('mousedown', (e) => {
      e.stopPropagation()
      this.startRotate(e, element)
    })
    element.appendChild(handleEl)
  }

  startResize(event, element, handle) {
    event.preventDefault()
    this.isResizing = true
    this.resizeHandle = handle
    this.selectedElement = element
    this._preventSelectionDuringInteract()

    const canvas = element.closest('.editor__canvas')
    const canvasRect = canvas.getBoundingClientRect()

    // Gebruik canvas-relative coördinaten (logische 1920x1080 ruimte)
    const scale = this.canvasScale || 1
    this.startX = (event.clientX - canvasRect.left) / scale
    this.startY = (event.clientY - canvasRect.top) / scale

    const img = element.querySelector('img')
    if (img) {
      // Gebruik de daadwerkelijke afbeelding dimensies
      this.startWidth = parseInt(img.style.width) || img.naturalWidth || img.offsetWidth
      this.startHeight = parseInt(img.style.height) || img.naturalHeight || img.offsetHeight
      this.startAspectRatio = this.startWidth / this.startHeight
    } else {
      // Gebruik style/offsetWidth zodat bij gedraaide elementen de echte dimensies
      // worden gebruikt (getBoundingClientRect geeft de omhullende rechthoek)
      this.startWidth = parseInt(element.style.width) || element.offsetWidth
      this.startHeight = parseInt(element.style.height) || element.offsetHeight
      this.startAspectRatio = this.startWidth / this.startHeight
    }

    // Start positie van het element
    this.startElementLeft = parseInt(element.style.left) || 0
    this.startElementTop = parseInt(element.style.top) || 0

    element.classList.add('editor__resizing')
    document.addEventListener('mousemove', this.mousemoveHandler)
    document.addEventListener('mouseup', this.mouseupHandler)
  }

  startRotate(event, element) {
    event.preventDefault()
    this.isRotating = true
    this.selectedElement = element
    this._preventSelectionDuringInteract()

    const rect = element.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    this.startX = centerX
    this.startY = centerY

    // Bestaande rotatie bepalen van de wrapper div (zodat achtergrond ook meedraait)
    const transform = element.style.transform || ''
    const rotationMatch = transform.match(/rotate\(([-\d.]+)deg\)/)
    this.startRotation = rotationMatch ? parseFloat(rotationMatch[1]) : 0

    const angle = Math.atan2(event.clientY - centerY, event.clientX - centerX)
    this.startAngle = angle

    document.addEventListener('mousemove', this.mousemoveHandler)
    document.addEventListener('mouseup', this.mouseupHandler)
  }

  handleMouseMove(event) {
    if (this.isResizing || this.isRotating) {
      event.preventDefault()
    }
    if (this.isResizing && this.selectedElement) {
      const canvas = this.selectedElement.closest('.editor__canvas')
      const canvasRect = canvas.getBoundingClientRect()
      const scale = this.canvasScale || 1
      // Coördinaten in logische 1920x1080 ruimte
      const currentX = (event.clientX - canvasRect.left) / scale
      const currentY = (event.clientY - canvasRect.top) / scale

      const deltaX = currentX - this.startX
      const deltaY = currentY - this.startY

      let newWidth = this.startWidth
      let newHeight = this.startHeight
      let newLeft = this.startElementLeft
      let newTop = this.startElementTop

      const handle = this.resizeHandle
      const img = this.selectedElement.querySelector('img')
      const elementType = this.selectedElement.dataset.elementType
      const isCornerHandle = handle.length === 2 // nw, ne, sw, se

      // Voor hoekhandles: behoud aspect ratio (tenzij Shift ingedrukt)
      if (isCornerHandle && (img || elementType === 'circle') && !event.shiftKey) {
        // Bereken de afstand van de muis tot de tegenoverliggende hoek
        // Dit geeft ons de nieuwe grootte direct
        let oppositeX, oppositeY

        if (handle === 'nw') {
          // Tegenoverliggende hoek is zuidoost
          oppositeX = this.startElementLeft + this.startWidth
          oppositeY = this.startElementTop + this.startHeight
        } else if (handle === 'ne') {
          // Tegenoverliggende hoek is zuidwest
          oppositeX = this.startElementLeft
          oppositeY = this.startElementTop + this.startHeight
        } else if (handle === 'sw') {
          // Tegenoverliggende hoek is noordoost
          oppositeX = this.startElementLeft + this.startWidth
          oppositeY = this.startElementTop
        } else if (handle === 'se') {
          // Tegenoverliggende hoek is noordwest
          oppositeX = this.startElementLeft
          oppositeY = this.startElementTop
        }

        // Bereken afstand tot tegenoverliggende hoek
        const distanceX = Math.abs(currentX - oppositeX)
        const distanceY = Math.abs(currentY - oppositeY)

        // Gebruik de grootste afstand en behoud aspect ratio
        if (distanceX / this.startAspectRatio > distanceY) {
          // X afstand is dominant
          newWidth = distanceX
          newHeight = distanceX / this.startAspectRatio
        } else {
          // Y afstand is dominant
          newHeight = distanceY
          newWidth = distanceY * this.startAspectRatio
        }

        // Bereken nieuwe positie op basis van de hoek
        if (handle === 'nw') {
          newLeft = oppositeX - newWidth
          newTop = oppositeY - newHeight
        } else if (handle === 'ne') {
          newLeft = oppositeX
          newTop = oppositeY - newHeight
        } else if (handle === 'sw') {
          newLeft = oppositeX - newWidth
          newTop = oppositeY
        } else if (handle === 'se') {
          newLeft = oppositeX
          newTop = oppositeY
        }
      } else {
        // Zijkant handles of Shift ingedrukt: vrij resizen
        if (handle.includes('e')) {
          newWidth = this.startWidth + deltaX
        }
        if (handle.includes('w')) {
          newWidth = this.startWidth - deltaX
          newLeft = this.startElementLeft + deltaX
        }
        if (handle.includes('s')) {
          newHeight = this.startHeight + deltaY
        }
        if (handle.includes('n')) {
          newHeight = this.startHeight - deltaY
          newTop = this.startElementTop + deltaY
        }

        // Voor afbeeldingen: behoud altijd verhoudingen, ook bij zijkant-handles (tenzij Shift)
        if (img && !event.shiftKey) {
          const centerX = this.startElementLeft + this.startWidth / 2
          const centerY = this.startElementTop + this.startHeight / 2

          if (handle.includes('e') || handle.includes('w')) {
            // Breedte bepaalt de hoogte
            const targetHeight = newWidth / this.startAspectRatio
            newHeight = targetHeight
            newTop = centerY - targetHeight / 2
          } else if (handle.includes('n') || handle.includes('s')) {
            // Hoogte bepaalt de breedte
            const targetWidth = newHeight * this.startAspectRatio
            newWidth = targetWidth
            newLeft = centerX - targetWidth / 2
          }
        }
      }

      // Minimum grootte
      newWidth = Math.max(20, newWidth)
      newHeight = Math.max(20, newHeight)

      // Maximum grootte (binnen canvas)
      const canvasWidth = canvas.offsetWidth
      const canvasHeight = canvas.offsetHeight
      newWidth = Math.min(newWidth, canvasWidth - newLeft)
      newHeight = Math.min(newHeight, canvasHeight - newTop)

      // Zorg dat element binnen canvas blijft
      if (newLeft < 0) {
        newWidth += newLeft
        newLeft = 0
      }
      if (newTop < 0) {
        newHeight += newTop
        newTop = 0
      }

      // Voor cirkels: breedte en hoogte gelijk houden
      if (elementType === 'circle') {
        // Cirkel: breedte = hoogte houden
        const size = Math.max(newWidth, newHeight)
        newWidth = size
        newHeight = size
      }

      if (img) {
        img.style.width = newWidth + 'px'
        img.style.height = newHeight + 'px'
        // Pas div aan aan nieuwe afbeelding afmetingen
        this.selectedElement.style.width = newWidth + 'px'
        this.selectedElement.style.height = newHeight + 'px'
      } else if (elementType === 'shape' || elementType === 'circle') {
        // Voor shapes en cirkels: direct breedte/hoogte op het element toepassen
        this.selectedElement.style.width = newWidth + 'px'
        this.selectedElement.style.height = newHeight + 'px'
      } else if (elementType === 'text') {
        this.selectedElement.style.width = newWidth + 'px'
        this.selectedElement.style.height = newHeight + 'px'
        const span = this.selectedElement.querySelector('span')
        if (span) {
          span.style.whiteSpace = 'normal'
          span.style.maxWidth = '100%'
        }
      }

      // Update element positie
      this.selectedElement.style.left = newLeft + 'px'
      this.selectedElement.style.top = newTop + 'px'
    }

    if (this.isRotating && this.selectedElement) {
      // Rotatie altijd op de wrapper div zetten zodat achtergrond ook meedraait
      const angle = Math.atan2(event.clientY - this.startY, event.clientX - this.startX)
      const rotation = this.startRotation + ((angle - this.startAngle) * 180 / Math.PI)

      this.selectedElement.style.transform = `rotate(${rotation}deg)`
    }
  }

  handleMouseUp(event) {
    if (this.isResizing) {
      if (this.selectedElement && this.selectedElement.dataset.elementType === 'text') {
        if (this.hasTextWidthTarget) this.textWidthTarget.value = this.selectedElement.style.width ? parseInt(this.selectedElement.style.width, 10) || '' : ''
        if (this.hasTextHeightTarget) this.textHeightTarget.value = this.selectedElement.style.height ? parseInt(this.selectedElement.style.height, 10) || '' : ''
      }
      this.isResizing = false
      this.resizeHandle = null
      if (this.selectedElement) {
        this.selectedElement.classList.remove('editor__resizing')
      }
    }

    if (this.isRotating) {
      this.isRotating = false
    }

    document.removeEventListener('mousemove', this.mousemoveHandler)
    document.removeEventListener('mouseup', this.mouseupHandler)
    this._restoreSelectionAfterInteract()
  }

  _preventSelectionDuringInteract() {
    document.body.classList.add('editor__no-select')
    if (!this._selectStartHandler) {
      this._selectStartHandler = (e) => {
        if (this.isResizing || this.isRotating) e.preventDefault()
      }
    }
    document.addEventListener('selectstart', this._selectStartHandler)
  }

  _restoreSelectionAfterInteract() {
    if (!this.isResizing && !this.isRotating) {
      document.body.classList.remove('editor__no-select')
      if (this._selectStartHandler) {
        document.removeEventListener('selectstart', this._selectStartHandler)
      }
    }
  }

  selectElement(event, element) {
    if (event) {
      event.stopPropagation()
    }

    // Verwijder selectie van vorig element
    if (this.selectedElement) {
      this.selectedElement.classList.remove('editor__selected')
    }

    this._removePanelRepositionListeners(this.selectedElement)

    this.selectedElement = element
    element.classList.add('editor__selected')

    // Toon properties panel
    this.propertiesPanelTarget.style.display = 'block'
    this.updatePanelPosition(element)

    const elementType = element.dataset.elementType

    if (elementType === 'text') {
      this.textPropertiesTarget.style.display = 'block'
      this.imagePropertiesTarget.style.display = 'none'
      this.generalPropertiesTarget.style.display = 'block'
      if (this.hasAnimationPropertiesTarget) this.animationPropertiesTarget.style.display = 'block'
      this.canvasPropertiesTarget.style.display = 'none'
      this.zIndexControlsTarget.style.display = 'block'
      if (this.hasActionsPropertiesTarget) this.actionsPropertiesTarget.style.display = 'block'

      const span = element.querySelector('span')
      if (span) {
        span.addEventListener('input', this._panelRepositionOnText)
        span.addEventListener('selectionchange', this._panelRepositionOnText)
        // Alleen bewerkbaar bij bewust klikken op de tekst; bij alleen selecteren niet
        span.contentEditable = event ? 'false' : 'true'

        this.textColorTarget.value = this.rgbToHex(span.style.color) || '#000000'
        const size = parseInt(span.style.fontSize) || 16
        this.fontSizeTarget.value = size
        if (this.hasFontSizeValueTarget) this.fontSizeValueTarget.textContent = size
        this.fontFamilyTarget.value = this._normalizeFontFamily(span.style.fontFamily) || 'Arial'

        // Bold en italic buttons
        const fontWeight = span.style.fontWeight || window.getComputedStyle(span).fontWeight
        const fontStyle = span.style.fontStyle || window.getComputedStyle(span).fontStyle

        if (fontWeight === 'bold' || parseInt(fontWeight) >= 700) {
          this.boldBtnTarget.classList.add('editor__active')
        } else {
          this.boldBtnTarget.classList.remove('editor__active')
        }

        if (fontStyle === 'italic') {
          this.italicBtnTarget.classList.add('editor__active')
        } else {
          this.italicBtnTarget.classList.remove('editor__active')
        }
        if (this.hasTextWidthTarget) this.textWidthTarget.value = element.style.width ? parseInt(element.style.width) || '' : ''
        if (this.hasTextHeightTarget) this.textHeightTarget.value = element.style.height ? parseInt(element.style.height) || '' : ''
      }
      this._openPanelSection('text')
    } else if (elementType === 'image') {
      this.textPropertiesTarget.style.display = 'none'
      this.imagePropertiesTarget.style.display = 'block'
      this.generalPropertiesTarget.style.display = 'block'
      if (this.hasAnimationPropertiesTarget) this.animationPropertiesTarget.style.display = 'block'
      this.canvasPropertiesTarget.style.display = 'none'
      this.zIndexControlsTarget.style.display = 'block'
      if (this.hasActionsPropertiesTarget) this.actionsPropertiesTarget.style.display = 'block'
      this._openPanelSection('image')
    } else {
      this.textPropertiesTarget.style.display = 'none'
      this.imagePropertiesTarget.style.display = 'none'
      this.generalPropertiesTarget.style.display = 'block'
      if (this.hasAnimationPropertiesTarget) this.animationPropertiesTarget.style.display = 'block'
      this.canvasPropertiesTarget.style.display = 'none'
      this.zIndexControlsTarget.style.display = 'block'
      if (this.hasActionsPropertiesTarget) this.actionsPropertiesTarget.style.display = 'block'
      this._openPanelSection('general')
    }

    // Algemene stijl voor alle elementen
    const computed = window.getComputedStyle(element)
    if (elementType === 'image') {
      // Bij afbeelding: kleur/slidertje sturen een gewone border op de <img> aan
      const img = element.querySelector('img')
      const imgComputed = img ? window.getComputedStyle(img) : computed
      const borderColor = imgComputed.borderTopColor || imgComputed.borderColor || imgComputed.backgroundColor
      this.elementBackgroundColorTarget.value = this.rgbToHex(borderColor)
      const borderWidth = parseInt(imgComputed.borderTopWidth) || 0
      this.elementPaddingTarget.value = borderWidth
      if (this.hasElementPaddingLabelTarget) {
        this.elementPaddingLabelTarget.textContent = 'Rand'
      }
    } else {
      if (this.hasElementPaddingLabelTarget) {
        this.elementPaddingLabelTarget.textContent = 'Padding'
      }
      const bg = computed.backgroundColor
      this.elementBackgroundColorTarget.value = this.rgbToHex(bg)
      const paddingTop = parseInt(computed.paddingTop) || 0
      this.elementPaddingTarget.value = paddingTop
    }

    // Padding-veld verbergen voor vormen en cirkels (kleurkiezer blijft zichtbaar)
    if (elementType === 'shape' || elementType === 'circle') {
      if (this.hasElementPaddingRowTarget) this.elementPaddingRowTarget.style.display = 'none'
    } else {
      if (this.hasElementPaddingRowTarget) this.elementPaddingRowTarget.style.display = ''
    }
    const radius = parseInt(computed.borderRadius) || 0
    this.elementBorderRadiusTarget.value = radius

    // Schaduw voor alle elementen
    if (this.hasShadowPropertiesTarget) {
      const boxShadow = computed.boxShadow || element.style.boxShadow || 'none'
      if (boxShadow !== 'none' && boxShadow) {
        // Parse box-shadow: offsetX offsetY blur color
        const shadowMatch = boxShadow.match(/([\d.-]+)px\s+([\d.-]+)px\s+([\d.-]+)px\s+(.+)/)
        if (shadowMatch) {
          const offsetX = parseFloat(shadowMatch[1]) || 0
          const offsetY = parseFloat(shadowMatch[2]) || 0
          const blur = parseFloat(shadowMatch[3]) || 0
          const color = shadowMatch[4].trim()

          // Bereken hoek uit offsetX en offsetY (in graden, 0 = rechts, 90 = onder)
          const angle = Math.atan2(offsetY, offsetX) * 180 / Math.PI
          const normalizedAngle = angle < 0 ? angle + 360 : angle

          this.elementShadowColorTarget.value = this.rgbToHex(color)
          this.elementShadowBlurTarget.value = Math.round(blur)
          this.elementShadowAngleTarget.value = Math.round(normalizedAngle)
        } else {
          this.elementShadowColorTarget.value = '#000000'
          this.elementShadowBlurTarget.value = 0
          this.elementShadowAngleTarget.value = 0
        }
      } else {
        this.elementShadowColorTarget.value = '#000000'
        this.elementShadowBlurTarget.value = 0
        this.elementShadowAngleTarget.value = 0
      }
    }

    // Algemene animaties voor alle elementen
    const animation = element.dataset.animation || ''
    this.animationTarget.value = animation
    if (this.hasAnimationDurationTarget) {
      const rawDur = element.dataset.animationDuration
      const parsedDur = rawDur !== undefined ? parseFloat(rawDur) : NaN
      const dur = Number.isNaN(parsedDur) ? 1 : Math.max(0, parsedDur)
      this.animationDurationTarget.value = dur
    }
    if (this.hasAnimationDelayTarget) {
      const rawDel = element.dataset.animationDelay
      const parsedDel = rawDel !== undefined ? parseFloat(rawDel) : NaN
      const del = Number.isNaN(parsedDel) ? 0 : Math.max(0, parsedDel)
      this.animationDelayTarget.value = del
    }
  }

  updatePanelPosition(element) {
    const panel = this.propertiesPanelTarget
    const editorRect = this.element.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const panelW = panel.offsetWidth || 280
    const panelH = panel.offsetHeight || 420
    const gap = 16
    const isText = element.dataset.elementType === 'text'

    // Coördinaten relatief aan de editor (panel is child van editor)
    let left = 0
    let top = 0

    if (isText) {
      // Bij tekstelementen: voorkeur onder het element (niet over de tekst)
      const belowTop = elementRect.bottom - editorRect.top + gap
      if (belowTop + panelH <= editorRect.height) {
        left = elementRect.left - editorRect.left
        top = belowTop
      } else {
        // Geen ruimte onder: rechts of links
        const rightLeft = elementRect.right - editorRect.left + gap
        if (rightLeft + panelW <= editorRect.width) {
          left = rightLeft
          top = elementRect.top - editorRect.top
        } else {
          left = elementRect.left - editorRect.left - panelW - gap
          top = elementRect.top - editorRect.top
        }
      }
    } else {
      // Niet-tekst: rechts van element, anders links
      left = elementRect.right - editorRect.left + gap
      top = elementRect.top - editorRect.top
      if (left + panelW > editorRect.width) {
        left = elementRect.left - editorRect.left - panelW - gap
      }
    }

    // Binnen editor houden
    left = Math.max(gap, Math.min(left, editorRect.width - panelW - gap))
    top = Math.max(gap, Math.min(top, editorRect.height - panelH - gap))

    panel.style.left = left + 'px'
    panel.style.top = top + 'px'
  }

  canvasClick(event) {
    // Duplicaat plaatsen: klik ergens op het canvas (inclusief op een element) plaatst de kopie
    if (this.pendingDuplicateElement) {
      const canvas = this.canvasTarget
      const canvasRect = canvas.getBoundingClientRect()
      const scale = this.canvasScale || 1
      const x = (event.clientX - canvasRect.left) / scale
      const y = (event.clientY - canvasRect.top) / scale
      this.placeDuplicateAt(x, y)
      event.stopPropagation()
      event.preventDefault()
      return
    }

    // Reageer als er op de "lege" canvas wordt geklikt (dus niet op een editable-element)
    const clickedOnCanvas =
      event.target === this.canvasTarget ||
      (event.target.closest('.editor__editable-element') === null &&
        event.target.closest('.editor__canvas') === this.canvasTarget)

    if (clickedOnCanvas) {
      if (this.activePlacementTool) {
        const canvas = this.canvasTarget
        const canvasRect = canvas.getBoundingClientRect()
        const scale = this.canvasScale || 1
        const x = (event.clientX - canvasRect.left) / scale
        const y = (event.clientY - canvasRect.top) / scale
        this.placeElementAt(x, y)
        return
      }
      // Verwijder selectie van elementen
      if (this.selectedElement) {
        this._removePanelRepositionListeners(this.selectedElement)
        this.selectedElement.classList.remove('editor__selected')
        this.selectedElement = null
      }
      // Verberg properties panel bij enkele klik
      this.propertiesPanelTarget.style.display = 'none'
    }
  }

  canvasDoubleClick(event) {
    // Reageer als er op de "lege" canvas wordt gedubbelklikt (dus niet op een editable-element)
    const clickedOnCanvas =
      event.target === this.canvasTarget ||
      (event.target.closest('.editor__editable-element') === null &&
        event.target.closest('.editor__canvas') === this.canvasTarget)

    if (clickedOnCanvas) {
      // Verwijder selectie van elementen
      if (this.selectedElement) {
        this._removePanelRepositionListeners(this.selectedElement)
        this.selectedElement.classList.remove('editor__selected')
        this.selectedElement = null
      }

      // Toon canvas properties panel bij dubbelklik
      this.showCanvasProperties(event)
    }
  }

  showCanvasProperties(event) {
    // Verberg alle andere properties
    this.textPropertiesTarget.style.display = 'none'
    this.imagePropertiesTarget.style.display = 'none'
    this.generalPropertiesTarget.style.display = 'none'
    if (this.hasAnimationPropertiesTarget) this.animationPropertiesTarget.style.display = 'none'
    if (this.hasShadowPropertiesTarget) this.shadowPropertiesTarget.style.display = 'none'
    this.zIndexControlsTarget.style.display = 'none'
    if (this.hasActionsPropertiesTarget) this.actionsPropertiesTarget.style.display = 'none'

    // Toon canvas properties
    this.canvasPropertiesTarget.style.display = 'block'
    this.propertiesPanelTarget.style.display = 'block'

    // Haal huidige achtergrondkleur op
    const currentBg = window.getComputedStyle(this.canvasTarget).backgroundColor
    const hexColor = this.rgbToHex(currentBg)
    this.canvasBackgroundColorTarget.value = hexColor
    // Achtergrondafbeelding: mode en verwijder-knop
    const mode = this.canvasTarget.dataset.backgroundImageMode || 'pattern'
    this.canvasBackgroundImageModeTarget.value = mode
    if (this.hasCanvasBackgroundImageRemoveTarget) {
      this.canvasBackgroundImageRemoveTarget.style.display = this.canvasTarget.dataset.backgroundImage ? 'inline-block' : 'none'
    }
    this.canvasBackgroundImageTarget.value = ''

    // Positioneer panel bij klik positie
    this.updateCanvasPanelPosition(event)

    this._openPanelSection('canvas')
  }

  updateCanvasPanelPosition(event) {
    const panel = this.propertiesPanelTarget
    const canvas = this.canvasTarget
    const canvasRect = canvas.getBoundingClientRect()

    // Positie panel bij klik positie
    let left = event.clientX - canvasRect.left + 20
    let top = event.clientY - canvasRect.top + 20

    // Zorg dat panel binnen canvas blijft
    if (left + 300 > canvasRect.width) {
      left = event.clientX - canvasRect.left - 320
    }
    if (top + 200 > canvasRect.height) {
      top = event.clientY - canvasRect.top - 200
    }
    if (left < 10) left = 10
    if (top < 10) top = 10

    panel.style.left = (left + canvas.scrollLeft) + 'px'
    panel.style.top = (top + canvas.scrollTop) + 'px'
  }

  updateCanvasBackgroundColor() {
    const color = this.canvasBackgroundColorTarget.value
    this.canvasTarget.style.backgroundColor = color
  }

  _applyCanvasBackgroundImage() {
    const canvas = this.canvasTarget
    const url = canvas.dataset.backgroundImage
    const mode = canvas.dataset.backgroundImageMode || 'pattern'
    if (!url) {
      canvas.style.backgroundImage = ''
      canvas.style.backgroundRepeat = ''
      canvas.style.backgroundSize = ''
      canvas.style.backgroundPosition = ''
      return
    }
    canvas.style.backgroundImage = `url(${url})`
    if (mode === 'pattern') {
      canvas.style.backgroundRepeat = 'repeat'
      canvas.style.backgroundSize = 'auto'
      canvas.style.backgroundPosition = '0 0'
    } else {
      canvas.style.backgroundRepeat = 'no-repeat'
      canvas.style.backgroundSize = mode === 'cover' ? 'cover' : 'contain'
      canvas.style.backgroundPosition = 'center'
    }
  }

  updateCanvasBackgroundImage() {
    const file = this.canvasBackgroundImageTarget.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => {
      this.canvasTarget.dataset.backgroundImage = reader.result
      this.canvasTarget.dataset.backgroundImageMode = this.canvasBackgroundImageModeTarget.value
      this._applyCanvasBackgroundImage()
      if (this.hasCanvasBackgroundImageRemoveTarget) {
        this.canvasBackgroundImageRemoveTarget.style.display = 'inline-block'
      }
    }
    reader.onerror = () => {
      console.error('Error processing canvas background image:', reader.error)
      alert('De afbeelding kon niet worden verwerkt.')
    }
    reader.readAsDataURL(file)
    this.canvasBackgroundImageTarget.value = ''
  }

  updateCanvasBackgroundImageMode() {
    if (!this.canvasTarget.dataset.backgroundImage) return
    this.canvasTarget.dataset.backgroundImageMode = this.canvasBackgroundImageModeTarget.value
    this._applyCanvasBackgroundImage()
  }

  removeCanvasBackgroundImage() {
    delete this.canvasTarget.dataset.backgroundImage
    delete this.canvasTarget.dataset.backgroundImageMode
    this._applyCanvasBackgroundImage()
    if (this.hasCanvasBackgroundImageRemoveTarget) {
      this.canvasBackgroundImageRemoveTarget.style.display = 'none'
    }
    this.canvasBackgroundImageModeTarget.value = 'pattern'
  }

  updateElementBackgroundColor() {
    if (!this.selectedElement) return
    const elementType = this.selectedElement.dataset.elementType
    const color = this.elementBackgroundColorTarget.value
    if (elementType === 'image') {
      const img = this.selectedElement.querySelector('img')
      if (img) {
        img.style.borderColor = color
        if (!img.style.borderStyle) {
          img.style.borderStyle = 'solid'
        }
      }
    } else {
      this.selectedElement.style.backgroundColor = color
    }
  }

  updateElementPadding() {
    if (!this.selectedElement) return
    const elementType = this.selectedElement.dataset.elementType
    // Padding is niet relevant voor shape- en circle-elementen
    if (elementType === 'shape' || elementType === 'circle') return
    const value = parseInt(this.elementPaddingTarget.value)
    const amount = isNaN(value) ? 0 : Math.max(0, value)
    if (elementType === 'image') {
      // Bij afbeelding: slider stuurt border-width aan op de <img> i.p.v. padding
      const img = this.selectedElement.querySelector('img')
      if (img) {
        img.style.borderWidth = amount + 'px'
        if (amount > 0 && !img.style.borderStyle) {
          img.style.borderStyle = 'solid'
        }
        // Als er nog geen kleur staat, gebruik de huidige waarde van de kleurkiezer
        if (!img.style.borderColor || img.style.borderColor === 'transparent') {
          img.style.borderColor = this.elementBackgroundColorTarget.value || '#ffffff'
        }
      }
    } else {
      this.selectedElement.style.padding = amount + 'px'
    }
  }

  updateElementBorderRadius() {
    if (!this.selectedElement) return
    const value = parseInt(this.elementBorderRadiusTarget.value)
    const radius = isNaN(value) ? 0 : Math.max(0, value)
    this.selectedElement.style.borderRadius = radius + 'px'
  }

  updateElementShadow() {
    if (!this.selectedElement) return
    const color = this.elementShadowColorTarget.value || '#000000'
    const blur = parseInt(this.elementShadowBlurTarget.value) || 0
    const angle = parseInt(this.elementShadowAngleTarget.value) || 0

    if (blur === 0) {
      this.selectedElement.style.boxShadow = 'none'
      return
    }

    // Converteer hoek (0-360 graden, 0 = rechts, 90 = onder) naar offsetX en offsetY
    // Gebruik een vaste afstand (bijv. blur / 2) voor de offset
    const offsetDistance = blur / 2
    const angleRad = (angle * Math.PI) / 180
    const offsetX = Math.cos(angleRad) * offsetDistance
    const offsetY = Math.sin(angleRad) * offsetDistance

    this.selectedElement.style.boxShadow = `${offsetX}px ${offsetY}px ${blur}px ${color}`
  }

  // Geeft de contentEditable root (span) van het geselecteerde tekstelement
  getEditableRoot() {
    if (!this.selectedElement || this.selectedElement.dataset.elementType !== 'text') return null
    return this.selectedElement.querySelector('span[contenteditable="true"]') || this.selectedElement.querySelector('span')
  }

  // Controleert of de huidige selectie binnen het tekstelement valt en niet leeg is
  hasSelectionInEditable() {
    const root = this.getEditableRoot()
    if (!root) return false
    const sel = window.getSelection()
    if (!sel.rangeCount) return false
    const range = sel.getRangeAt(0)
    if (range.collapsed) return false
    return root.contains(range.commonAncestorContainer)
  }

  // Past de gegeven stijl toe op de geselecteerde tekst (wrap in span) of op het hele element
  applyTextStyle(styleObject) {
    const root = this.getEditableRoot()
    if (!root) return
    if (this.hasSelectionInEditable()) {
      const sel = window.getSelection()
      const range = sel.getRangeAt(0)
      const span = document.createElement('span')
      Object.assign(span.style, styleObject)
      try {
        range.surroundContents(span)
      } catch (_) {
        const contents = range.extractContents()
        span.appendChild(contents)
        range.insertNode(span)
      }
    } else {
      Object.assign(root.style, styleObject)
    }
  }

  updateTextColor() {
    if (!this.selectedElement) return
    const color = this.textColorTarget.value
    this.applyTextStyle({ color })
  }

  updateFontSize() {
    if (!this.selectedElement) return
    const root = this.getEditableRoot()
    if (!root) return
    const value = this.fontSizeTarget.value
    const fontSize = value + 'px'
    if (this.hasFontSizeValueTarget) this.fontSizeValueTarget.textContent = value
    if (this.hasSelectionInEditable()) {
      this.applyTextStyle({ fontSize })
    } else {
      root.style.fontSize = fontSize
      root.querySelectorAll('span').forEach(span => {
        if (span !== root) span.style.fontSize = ''
      })
    }
  }

  updateFontFamily() {
    if (!this.selectedElement) return
    const fontFamily = this.fontFamilyTarget.value
    this.applyTextStyle({ fontFamily })
  }

  _syncTextElementWrap(element) {
    const span = element && element.querySelector('span')
    if (!span) return
    const hasWidth = element.style.width && element.style.width !== ''
    span.style.whiteSpace = hasWidth ? 'normal' : 'nowrap'
    span.style.maxWidth = hasWidth ? '100%' : ''
  }

  updateTextDimensions() {
    if (!this.selectedElement || this.selectedElement.dataset.elementType !== 'text') return
    const w = this.hasTextWidthTarget ? this.textWidthTarget.value.trim() : ''
    const h = this.hasTextHeightTarget ? this.textHeightTarget.value.trim() : ''
    if (w !== '') {
      const num = parseInt(w, 10)
      if (!isNaN(num) && num > 0) this.selectedElement.style.width = num + 'px'
    } else {
      this.selectedElement.style.width = ''
    }
    if (h !== '') {
      const num = parseInt(h, 10)
      if (!isNaN(num) && num > 0) this.selectedElement.style.height = num + 'px'
    } else {
      this.selectedElement.style.height = ''
    }
    this._syncTextElementWrap(this.selectedElement)
  }

  toggleBold() {
    if (!this.selectedElement) return
    const root = this.getEditableRoot()
    if (!root) return
    if (this.hasSelectionInEditable()) {
      const sel = window.getSelection()
      const range = sel.getRangeAt(0)
      const node = range.commonAncestorContainer
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
      const currentBold = el && (el.style.fontWeight === 'bold' || parseInt(window.getComputedStyle(el).fontWeight) >= 700)
      this.applyTextStyle({ fontWeight: currentBold ? 'normal' : 'bold' })
      this.boldBtnTarget.classList.toggle('editor__active', !currentBold)
      return
    }
    const isBold = root.style.fontWeight === 'bold' ||
      window.getComputedStyle(root).fontWeight === 'bold' ||
      parseInt(window.getComputedStyle(root).fontWeight) >= 700
    root.style.fontWeight = isBold ? 'normal' : 'bold'
    this.boldBtnTarget.classList.toggle('editor__active', !isBold)
  }

  toggleItalic() {
    if (!this.selectedElement) return
    const root = this.getEditableRoot()
    if (!root) return
    if (this.hasSelectionInEditable()) {
      const sel = window.getSelection()
      const range = sel.getRangeAt(0)
      const node = range.commonAncestorContainer
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
      const currentItalic = el && window.getComputedStyle(el).fontStyle === 'italic'
      this.applyTextStyle({ fontStyle: currentItalic ? 'normal' : 'italic' })
      this.italicBtnTarget.classList.toggle('editor__active', !currentItalic)
      return
    }
    const isItalic = root.style.fontStyle === 'italic'
    root.style.fontStyle = isItalic ? 'normal' : 'italic'
    this.italicBtnTarget.classList.toggle('editor__active', !isItalic)
  }

  updateImage() {
    if (!this.selectedElement) return
    const file = this.imageFileTarget.files[0]
    if (file) {
      const img = this.selectedElement.querySelector('img')
      if (!img) return
      if (this.hasUploadUrlValue && this.uploadUrlValue && this.uploadUrlValue.trim()) {
        this._uploadImage(file).then((url) => {
          img.src = url
          this._scheduleSerialize()
        }).catch((err) => {
          console.error('Error uploading image:', err)
          alert(err.message || 'De afbeelding kon niet worden geüpload.')
        })
      } else {
        const reader = new FileReader()
        reader.onloadend = () => {
          img.src = reader.result
          this._scheduleSerialize()
        }
        reader.onerror = () => {
          console.error('Error reading image:', reader.error)
          alert('De afbeelding kon niet worden gelezen.')
        }
        reader.readAsDataURL(file)
      }
    }
  }

  updateAnimation() {
    if (!this.selectedElement) return

    // Verwijder alle animatie classes
    this.selectedElement.classList.remove(
      'editor__animate-fadeIn', 'editor__animate-slideInLeft', 'editor__animate-slideInRight',
      'editor__animate-slideInUp', 'editor__animate-slideInDown', 'editor__animate-zoomIn', 'editor__animate-spin'
    )

    const animation = this.animationTarget.value

    if (animation) {
      this.selectedElement.classList.add(`editor__animate-${animation}`)
      this.selectedElement.dataset.animation = animation
    } else {
      delete this.selectedElement.dataset.animation
    }

    this.updateAnimationTiming()
  }

  updateAnimationTiming() {
    if (!this.selectedElement) return
    const rawDuration = this.animationDurationTarget?.value
    const parsedDuration = rawDuration !== undefined && rawDuration !== '' ? parseFloat(rawDuration) : NaN
    const duration = Number.isNaN(parsedDuration) ? 1 : Math.max(0, parsedDuration)
    const rawDelay = this.animationDelayTarget?.value
    const parsedDelay = rawDelay !== undefined && rawDelay !== '' ? parseFloat(rawDelay) : NaN
    const delay = Number.isNaN(parsedDelay) ? 0 : Math.max(0, parsedDelay)

    this.selectedElement.style.animationDuration = `${duration}s`
    this.selectedElement.style.animationDelay = `${delay}s`
    this.selectedElement.dataset.animationDuration = duration
    this.selectedElement.dataset.animationDelay = delay
  }

  deleteElement() {
    if (!this.selectedElement) return
    this._removePanelRepositionListeners(this.selectedElement)
    this.selectedElement.remove()
    this.selectedElement = null
    this.propertiesPanelTarget.style.display = 'none'
  }

  moveForward() {
    if (!this.selectedElement) return

    const canvas = this.canvasTarget
    const allElements = Array.from(canvas.querySelectorAll('.editor__editable-element'))
    const currentIndex = allElements.indexOf(this.selectedElement)

    if (currentIndex < allElements.length - 1) {
      // Verplaats element naar voren in de DOM (hogere z-index)
      const nextElement = allElements[currentIndex + 1]
      nextElement.parentNode.insertBefore(this.selectedElement, nextElement.nextSibling)
    } else {
      // Al op de top, verhoog z-index
      const currentZ = parseInt(this.selectedElement.style.zIndex) || 0
      const maxZ = Math.max(...allElements.map(el => parseInt(el.style.zIndex) || 0), 0)
      this.selectedElement.style.zIndex = maxZ + 1
    }
  }

  moveBackward() {
    if (!this.selectedElement) return

    const canvas = this.canvasTarget
    const allElements = Array.from(canvas.querySelectorAll('.editor__editable-element'))
    const currentIndex = allElements.indexOf(this.selectedElement)

    if (currentIndex > 0) {
      // Verplaats element naar achteren in de DOM (lagere z-index)
      const prevElement = allElements[currentIndex - 1]
      prevElement.parentNode.insertBefore(this.selectedElement, prevElement)
    } else {
      // Al op de bottom, verlaag z-index
      const currentZ = parseInt(this.selectedElement.style.zIndex) || 0
      const minZ = Math.min(...allElements.map(el => parseInt(el.style.zIndex) || 0), 0)
      this.selectedElement.style.zIndex = minZ - 1
    }
  }

  rgbToHex(rgb) {
    if (!rgb || rgb === '' || rgb === 'transparent') return '#ffffff'
    if (rgb.startsWith('#')) return rgb

    // Ondersteun zowel rgb() als rgba()
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    if (match) {
      const r = parseInt(match[1]).toString(16).padStart(2, '0')
      const g = parseInt(match[2]).toString(16).padStart(2, '0')
      const b = parseInt(match[3]).toString(16).padStart(2, '0')
      return `#${r}${g}${b}`
    }
    return '#ffffff'
  }

  /**
   * Serialiseert alle canvas-elementen naar schone HTML (zonder editor-handles, -classes, -data).
   * Afbeeldingen: src blijft als URL (van upload) of data-URL.
   * Schrijft het resultaat naar het hidden field canvasOutput.
   */
  serializeCanvas() {
    if (!this.hasCanvasOutputTarget || !this.canvasTarget) return
    const canvas = this.canvasTarget
    const backgroundColor = this.rgbToHex(window.getComputedStyle(canvas).backgroundColor)
    const elements = canvas.querySelectorAll('.editor__editable-element')
    const parts = []
    for (const el of elements) {
      const clone = el.cloneNode(true)
      // Verwijder resize- en rotate-handles
      clone.querySelectorAll('.editor__resize-handle, .editor__rotate-handle').forEach(h => h.remove())
      // Strip editor-specifieke data-attributen; behoud type en animatie voor weergave
      const elementType = clone.dataset.elementType
      const animation = clone.dataset.animation
      const animationDuration = clone.dataset.animationDuration
      const animationDelay = clone.dataset.animationDelay
      Object.keys(clone.dataset).forEach(key => { delete clone.dataset[key] })
      if (elementType) clone.dataset.elementType = elementType
      if (animation) clone.dataset.animation = animation
      if (animationDuration) clone.dataset.animationDuration = animationDuration
      if (animationDelay) clone.dataset.animationDelay = animationDelay
      // Verwijder alle editor__-classes (inclusief animatie-class; weergave kan die uit data-animation halen)
      clone.className = Array.from(clone.classList)
        .filter(c => !c.startsWith('editor__'))
        .join(' ')
      // Tekst: contenteditable uitzetten voor export (gebruik origineel el voor type-check)
      if (el.dataset.elementType === 'text') {
        clone.querySelectorAll('[contenteditable="true"]').forEach(span => { span.contentEditable = 'false' })
      }
      // will-change niet meenemen
      clone.style.willChange = ''
      // Afbeeldingen: src blijft als URL (geen base64-conversie meer)
      const img = clone.querySelector('img')
      if (img) {
        // Unwrap img uit editor__image-wrap voor schone export (wrap wordt bij laden opnieuw toegevoegd)
        const wrap = clone.querySelector('.editor__image-wrap')
        if (wrap && wrap.parentNode) {
          wrap.parentNode.insertBefore(img, wrap)
          wrap.remove()
        }
      }
      // Strip editor__-classes ook van descendants
      clone.querySelectorAll('[class*="editor__"]').forEach(desc => {
        desc.className = Array.from(desc.classList)
          .filter(c => !c.startsWith('editor__'))
          .join(' ')
      })
      // Style-attribuut herschrijven met font-family zonder quotes, zodat outerHTML geen &quot; produceert
      clone.querySelectorAll('[style]').forEach(node => {
        const parts = []
        for (let i = 0; i < node.style.length; i++) {
          const prop = node.style[i]
          let val = node.style.getPropertyValue(prop)
          if (prop === 'font-family') val = this._normalizeFontFamily(val) || val
          parts.push(prop + ': ' + val)
        }
        if (parts.length) node.setAttribute('style', parts.join('; '))
      })
      parts.push(clone.outerHTML)
    }
    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-canvas-background', backgroundColor)
    const bgImage = canvas.dataset.backgroundImage
    const bgImageMode = canvas.dataset.backgroundImageMode
    if (bgImage) {
      wrapper.setAttribute('data-canvas-background-image', bgImage)
      wrapper.setAttribute('data-canvas-background-image-mode', bgImageMode || 'pattern')
    }
    wrapper.innerHTML = parts.join('')
    this.canvasOutputTarget.value = wrapper.outerHTML
  }

  _scheduleSerialize() {
    if (this._serializeDebounce) clearTimeout(this._serializeDebounce)
    this._serializeDebounce = setTimeout(() => {
      this._serializeDebounce = null
      this.serializeCanvas()
    }, 300)
  }

}